import {
  CloudUpload,
  ContentCopy,
  ErrorOutline,
  ExpandMore,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChangeEvent, useMemo, useRef, useState } from 'react';
import {
  DbEtlRunLog,
  usePostApiBillingImportsMutation,
} from '../../../../app/athenaApiGen';

// Deltek invoice CSV import — POST /api/billing/imports.
// The backend truncates StagingDeltekBilling, streams the CSV via Postgres COPY,
// runs RunBillingETL, and returns an ETLRunLog audit row. Synchronous: the
// request blocks until ETL completes (can take minutes on large files).
//
// Frontend responsibilities:
//   • Pre-upload confirm dialog — the run is destructive.
//   • File-size guard — backend hard cap is 100 MB (returns 413). Warn at 80 MB.
//   • Surface row counts on success and structured errorCode/Message/Detail on failure.

const MAX_BYTES = 100 * 1024 * 1024;
const WARN_BYTES = 80 * 1024 * 1024;

const ROW_COUNT_FIELDS: Array<{ key: keyof DbEtlRunLog; label: string }> = [
  { key: 'rowsLoaded', label: 'Total rows loaded (staging)' },
  { key: 'rowsInvoices', label: 'Invoices' },
  { key: 'rowsLineItems', label: 'Invoice line items' },
  { key: 'rowsCustomers', label: 'Customers' },
  { key: 'rowsEmployees', label: 'Employees' },
  { key: 'rowsAssignments', label: 'Assignments' },
  { key: 'rowsCandWorkflow', label: 'Candidate workflow' },
  { key: 'rowsCategories', label: 'Categories' },
  { key: 'rowsCatimpact', label: 'Category impact' },
  { key: 'rowsClosures', label: 'Closures' },
  { key: 'rowsImpacts', label: 'Impacts' },
  { key: 'rowsOpenReqs', label: 'Open requisitions' },
  { key: 'rowsPendingHires', label: 'Pending hires' },
  { key: 'rowsProbabilities', label: 'Probabilities' },
  { key: 'rowsRejects', label: 'Rejects' },
  { key: 'rowsResources', label: 'Resources' },
  { key: 'rowsResourceTraining', label: 'Resource training' },
  { key: 'rowsRisks', label: 'Risks' },
  { key: 'rowsTrainingHdr', label: 'Training header' },
  { key: 'rowsTrainingType', label: 'Training type' },
];

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const formatTimestamp = (iso?: string): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const formatDuration = (startedAt?: string, finishedAt?: string): string => {
  if (!startedAt || !finishedAt) return '—';
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (Number.isNaN(ms) || ms < 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
};

interface ParsedError {
  errorCode?: string;
  errorMessage?: string;
  errorDetail?: string;
  runId?: number;
  httpStatus?: number;
  raw: string;
}

const parseError = (err: unknown): ParsedError => {
  // RTK Query error shape: { status: number | 'FETCH_ERROR' | ..., data?: any }
  const e = err as { status?: number | string; data?: unknown };
  const httpStatus = typeof e?.status === 'number' ? e.status : undefined;
  const data = e?.data;
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    return {
      errorCode: typeof d.errorCode === 'string' ? d.errorCode : undefined,
      errorMessage: typeof d.errorMessage === 'string' ? d.errorMessage : undefined,
      errorDetail: typeof d.errorDetail === 'string' ? d.errorDetail : undefined,
      runId: typeof d.runId === 'number' ? d.runId : undefined,
      httpStatus,
      raw: JSON.stringify(data, null, 2),
    };
  }
  return {
    httpStatus,
    raw: typeof data === 'string' ? data : JSON.stringify(err, null, 2),
  };
};

const AdminInvoiceUploadPanel = () => {
  const [file, setFile] = useState<File | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<DbEtlRunLog | null>(null);
  const [parsedErr, setParsedErr] = useState<ParsedError | null>(null);
  const [copySnack, setCopySnack] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [postBillingImport, { isLoading }] = usePostApiBillingImportsMutation();

  const onPickFile = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setResult(null);
    setParsedErr(null);
    // Allow re-picking the same file after a failed upload.
    if (inputRef.current) inputRef.current.value = '';
  };

  const clearFile = () => {
    setFile(null);
    setResult(null);
    setParsedErr(null);
  };

  const overMax = file ? file.size > MAX_BYTES : false;
  const overWarn = file ? file.size > WARN_BYTES : false;

  const canUpload = !!file && !overMax && !isLoading;

  const runUpload = async () => {
    if (!file) return;
    setConfirmOpen(false);
    setResult(null);
    setParsedErr(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      // Codegen typed `body` as `{ file: Blob }` (multipart-form-data field).
      // RTK Query's fetchBaseQuery accepts FormData directly — cast through.
      const data = await postBillingImport({
        body: formData as unknown as { file: Blob },
      }).unwrap();
      setResult(data);
    } catch (err) {
      setParsedErr(parseError(err));
    }
  };

  const rowCounts = useMemo(() => {
    if (!result) return [];
    return ROW_COUNT_FIELDS.map(({ key, label }) => ({
      label,
      value: (result[key] as number | undefined) ?? 0,
    }));
  }, [result]);

  const copyErrorRaw = async () => {
    if (!parsedErr) return;
    try {
      await navigator.clipboard.writeText(parsedErr.raw);
      setCopySnack(true);
    } catch {
      /* clipboard may be unavailable in some browsers/contexts */
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="warning" variant="outlined">
        Uploading a CSV here <strong>truncates the StagingDeltekBilling table
        and re-runs the full billing ETL</strong>. The upload is synchronous and
        may take several minutes on large files.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2">
            Deltek Invoice CSV (UTF-8, 44 columns; max 100 MB)
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              component="label"
              disabled={isLoading}
            >
              Choose CSV
              <input
                ref={inputRef}
                hidden
                type="file"
                accept=".csv,text/csv"
                onChange={onPickFile}
              />
            </Button>

            {file && (
              <>
                <Chip
                  label={file.name}
                  variant="outlined"
                  onDelete={isLoading ? undefined : clearFile}
                />
                <Chip
                  label={formatBytes(file.size)}
                  size="small"
                  color={overMax ? 'error' : overWarn ? 'warning' : 'default'}
                />
              </>
            )}
          </Stack>

          {overMax && (
            <Alert severity="error">
              File exceeds the 100 MB upload limit. The backend will reject it
              with a 413 response. Split the export or use the manual ETL path.
            </Alert>
          )}
          {!overMax && overWarn && (
            <Alert severity="warning">
              File is over 80 MB. Upload may be slow and is close to the 100 MB
              hard cap.
            </Alert>
          )}

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              disabled={!canUpload}
              onClick={() => setConfirmOpen(true)}
            >
              Upload & Run ETL
            </Button>
          </Stack>

          {isLoading && (
            <Box>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary">
                Streaming file to staging and running ETL. Please do not
                navigate away.
              </Typography>
            </Box>
          )}
        </Stack>
      </Paper>

      {result && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle1">ETL run complete</Typography>
              <Chip
                size="small"
                color={result.status === 'success' ? 'success' : 'warning'}
                label={`Status: ${result.status ?? 'unknown'}`}
              />
              {result.runId !== undefined && (
                <Chip size="small" label={`Run ID: ${result.runId}`} />
              )}
              {result.runType && (
                <Chip size="small" variant="outlined" label={result.runType} />
              )}
            </Stack>

            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Typography variant="body2">
                <strong>Started:</strong> {formatTimestamp(result.startedAt)}
              </Typography>
              <Typography variant="body2">
                <strong>Finished:</strong> {formatTimestamp(result.finishedAt)}
              </Typography>
              <Typography variant="body2">
                <strong>Duration:</strong>{' '}
                {formatDuration(result.startedAt, result.finishedAt)}
              </Typography>
            </Stack>

            <Accordion disableGutters defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="body2">Row counts by table</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Table</TableCell>
                        <TableCell align="right">Rows</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rowCounts.map((r) => (
                        <TableRow key={r.label}>
                          <TableCell>{r.label}</TableCell>
                          <TableCell align="right">
                            {r.value.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Paper>
      )}

      {parsedErr && (
        <Paper
          variant="outlined"
          sx={{ p: 2, borderColor: 'error.main' }}
        >
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ErrorOutline color="error" />
              <Typography variant="subtitle1" color="error">
                ETL upload failed
              </Typography>
              {parsedErr.httpStatus !== undefined && (
                <Chip
                  size="small"
                  color="error"
                  label={`HTTP ${parsedErr.httpStatus}`}
                />
              )}
              {parsedErr.runId !== undefined && (
                <Chip size="small" label={`Run ID: ${parsedErr.runId}`} />
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Tooltip title="Copy raw response">
                <IconButton size="small" onClick={copyErrorRaw}>
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            {parsedErr.errorCode && (
              <Typography variant="body2">
                <strong>Error code:</strong> {parsedErr.errorCode}
              </Typography>
            )}
            {parsedErr.errorMessage && (
              <Typography variant="body2">
                <strong>Message:</strong> {parsedErr.errorMessage}
              </Typography>
            )}
            {parsedErr.errorDetail && (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Detail:</strong>
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    overflow: 'auto',
                    maxHeight: 240,
                  }}
                >
                  {parsedErr.errorDetail}
                </Box>
              </Box>
            )}
            {!parsedErr.errorCode
              && !parsedErr.errorMessage
              && !parsedErr.errorDetail && (
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  overflow: 'auto',
                  maxHeight: 240,
                }}
              >
                {parsedErr.raw}
              </Box>
            )}
          </Stack>
        </Paper>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Deltek invoice upload</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This will:
            <ul>
              <li>
                <strong>Truncate</strong> the StagingDeltekBilling table.
              </li>
              <li>Stream the selected CSV into staging.</li>
              <li>Run the full billing ETL pipeline.</li>
            </ul>
            {file && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                File: <strong>{file.name}</strong> ({formatBytes(file.size)})
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={runUpload}
            disabled={isLoading}
          >
            Upload &amp; Run ETL
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySnack}
        autoHideDuration={2500}
        onClose={() => setCopySnack(false)}
        message="Raw error response copied to clipboard."
      />
    </Box>
  );
};

export default AdminInvoiceUploadPanel;
